"use client";

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import type { MenusOutput } from "@/interfaces/menus";
import { LoadingSpinner } from "@/components/LoadingSpinner";
import {
  Trash,
  Plus,
  Filter,
  X,
  Edit,
  Book,
  ChevronLeft,
  ChevronRight,
  Store,
  Eye,
  EyeOff,
  Truck,
} from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "@/components/ui/sheet";
import {
  Card,
  CardContent,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { MenusService } from "@/services/MenusService";
import { useDeleteConfirmation } from "@/hooks/use-delete-confirmation";
import { DeleteConfirmationAlert } from "@/components/DeleteConfirmationAlert";
import type { PaginatedResponse, Pagination } from "@/interfaces";
import { cn } from "@/lib/utils";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

type FilterType = "query";

interface FilterItem {
  type: FilterType;
  value: string;
}

const FilterContent = ({
  addFilter,
  onClose,
}: {
  addFilter: (type: FilterType, value: string) => void;
  onClose?: () => void;
}) => (
  <div className="space-y-4">
    <div>
      <h4 className="font-medium mb-2">Nome</h4>
      <div className="flex space-x-2">
        <Input
          placeholder="Digite o nome"
          className="h-8"
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              const value = (e.target as HTMLInputElement).value;
              if (value) {
                addFilter("query", value);
                onClose?.();
                (e.target as HTMLInputElement).value = "";
              }
            }
          }}
        />
      </div>
    </div>
  </div>
);

interface MenuCardProps {
  menu: MenusOutput;
  onEdit: () => void;
  onDelete: () => void;
}

const MenuCard = ({
  menu,
  onEdit,
  onDelete,
}: MenuCardProps) => {
  const deliveryPriceFormatter = new Intl.NumberFormat("pt-BR", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
  const formattedDeliveryPrice = deliveryPriceFormatter.format(
    menu.minDeliveryPrice ?? 0
  );

  return (
    <Card className="flex h-full flex-col border border-gray-100 shadow-sm">
      <CardHeader className="space-y-3">
        <div className="flex items-start justify-between gap-3">
          <div className="space-y-1">
            <CardTitle className="text-lg font-semibold text-gray-900">
              {menu.name}
            </CardTitle>
          </div>
          <Badge
            variant={menu.isVisible ? "default" : "secondary"}
            className={cn(
              "flex items-center gap-1 px-2 py-1 text-xs",
              menu.isVisible
                ? "bg-green-600 hover:bg-green-700"
                : "bg-gray-200 text-gray-700"
            )}
          >
            {menu.isVisible ? (
              <Eye className="h-3.5 w-3.5" />
            ) : (
              <EyeOff className="h-3.5 w-3.5" />
            )}
            {menu.isVisible ? "Visível" : "Oculto"}
          </Badge>
        </div>

        <div className="flex flex-wrap gap-3 text-sm text-gray-600">
          <div className="flex items-center gap-2">
            <Store className="h-4 w-4 text-[#EE7B11]" />
            <span>
              {menu.acceptDelivery
                ? "Entrega e retirada disponíveis"
                : "Apenas retirada no local"}
            </span>
          </div>
          {menu.acceptDelivery && (
            <div className="flex items-center gap-2">
              <Truck className="h-4 w-4 text-[#EE7B11]" />
              <span>Taxa mínima: R${formattedDeliveryPrice}</span>
            </div>
          )}
        </div>
      </CardHeader>

      <CardContent className="flex-1">
        {/* Conteúdo adicional pode ser adicionado aqui no futuro */}
      </CardContent>

      <CardFooter className="flex flex-col gap-3 border-t bg-gray-50 p-4 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-center gap-2 text-xs text-muted-foreground">
          <span>Formas de pagamento:</span>
          <span className="font-medium text-gray-700">
            {menu.allowedPaymentMethods?.length ?? 0}
          </span>
        </div>
        <div className="flex w-full flex-wrap justify-end gap-2 sm:w-auto">
          <Button
            variant="outline"
            size="sm"
            onClick={onEdit}
            className="border-[#EE7B11] text-[#EE7B11] hover:bg-[#EE7B11]/10"
          >
            <Edit className="mr-1 h-4 w-4" />
            Editar
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={onDelete}
            className="border-red-500 text-red-500 hover:bg-red-50"
          >
            <Trash className="mr-1 h-4 w-4" />
            Excluir
          </Button>
        </div>
      </CardFooter>
    </Card>
  );
};

// Corrigir o problema de exibição "0 a 0 de 0 catálogos" quando há catálogos
// Modificar o componente PaginationControls para exibir corretamente os valores

const PaginationControls = ({
  pagination,
  onPageChange,
  onPageSizeChange,
}: {
  pagination: Pagination;
  onPageChange: (page: number) => void;
  onPageSizeChange: (pageSize: number) => void;
}) => {
  if (!pagination) {
    return null; // Retorna null se pagination for undefined
  }

  const { page, pageSize, total, pages } = pagination;
  const startItem = total > 0 ? (page - 1) * pageSize + 1 : 0;
  const endItem = total > 0 ? Math.min(page * pageSize, total) : 0;

  return (
    <div className="flex flex-col sm:flex-row items-center justify-between gap-4 mt-4 px-2">
      <div className="text-sm text-gray-500">
        Mostrando {startItem} a {endItem} de {total} catálogos
      </div>

      <div className="flex items-center gap-2">
        <div className="flex items-center mr-4">
          <span className="text-sm text-gray-500 mr-2">Itens por página:</span>
          <Select
            value={pageSize.toString()}
            onValueChange={(value) => onPageSizeChange(Number(value))}
          >
            <SelectTrigger className="h-8 w-[70px]">
              <SelectValue placeholder={pageSize.toString()} />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="5">5</SelectItem>
              <SelectItem value="10">10</SelectItem>
              <SelectItem value="20">20</SelectItem>
              <SelectItem value="50">50</SelectItem>
            </SelectContent>
          </Select>
        </div>

        <div className="flex items-center gap-1">
          <Button
            variant="outline"
            size="icon"
            className="h-8 w-8"
            onClick={() => onPageChange(page - 1)}
            disabled={page <= 1}
          >
            <ChevronLeft className="h-4 w-4" />
          </Button>

          <div className="flex items-center gap-1">
            {pages === 0 ? (
              <Button
                variant="default"
                size="icon"
                className="h-8 w-8 bg-[#EE7B11] hover:bg-[#d06a0a]"
              >
                1
              </Button>
            ) : (
              Array.from(
                { length: Math.max(1, Math.min(pages, 5)) },
                (_, i) => {
                  let pageNumber: number;

                  if (pages <= 5) {
                    pageNumber = i + 1;
                  } else if (page <= 3) {
                    pageNumber = i + 1;
                  } else if (page >= pages - 2) {
                    pageNumber = pages - 4 + i;
                  } else {
                    pageNumber = page - 2 + i;
                  }

                  return (
                    <Button
                      key={pageNumber}
                      variant={page === pageNumber ? "default" : "outline"}
                      size="icon"
                      className={cn(
                        "h-8 w-8",
                        page === pageNumber && "bg-[#EE7B11] hover:bg-[#d06a0a]"
                      )}
                      onClick={() => onPageChange(pageNumber)}
                    >
                      {pageNumber}
                    </Button>
                  );
                }
              )
            )}
          </div>

          <Button
            variant="outline"
            size="icon"
            className="h-8 w-8"
            onClick={() => onPageChange(page + 1)}
            disabled={page >= pages}
          >
            <ChevronRight className="h-4 w-4" />
          </Button>
        </div>
      </div>
    </div>
  );
};

const Menus = () => {
  const [menus, setMenus] = useState<MenusOutput[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [filters, setFilters] = useState<FilterItem[]>([]);
  const [isFilterSheetOpen, setIsFilterSheetOpen] = useState(false);
  const [pagination, setPagination] = useState<Pagination>({
    page: 1,
    pageSize: 10,
    total: 0,
    pages: 0,
    links: {
      self: "",
    },
  });

  const isFetchingRef = useRef(false);

  const navigate = useNavigate();
  const service = useMemo(() => new MenusService(), []);
  const currentPage = pagination.page;
  const currentPageSize = pagination.pageSize;
  const {
    isOpen,
    itemToDelete,
    open: openDeleteDialog,
    close: closeDeleteDialog,
  } = useDeleteConfirmation<string>();

  const fetchMenus = useCallback(async () => {
    if (isFetchingRef.current) return;

    try {
      isFetchingRef.current = true;
      setIsLoading(true);
      const queryParams: Record<string, string | number> = {
        page: currentPage,
        pageSize: currentPageSize,
      };

      filters.forEach((filter) => {
        queryParams[filter.type] = filter.value;
      });

      const response: PaginatedResponse<MenusOutput> = await service.findAll(
        queryParams
      );

      setMenus(response.data);

      if (
        response.pagination.pages > 0 &&
        currentPage > response.pagination.pages
      ) {
        setPagination((prev) => ({
          ...prev,
          page: response.pagination.pages,
        }));
      } else {
        setPagination(response.pagination);
      }
    } catch {
      if (currentPage !== 1) {
        setPagination((prev) => ({
          ...prev,
          page: 1,
        }));
      }
    } finally {
      setIsLoading(false);
      isFetchingRef.current = false;
    }
  }, [
    currentPage,
    currentPageSize,
    filters,
    service,
  ]);

  useEffect(() => {
    fetchMenus();
  }, [fetchMenus]);

  const handleNavigate = (menuId?: string) => {
    if (menuId) {
      navigate(`/catalogos/${menuId}/editar`);
    } else {
      navigate(`/catalogos/novo`);
    }
  };

  const handleDelete = (id: string) => {
    openDeleteDialog(id);
  };

  const handleConfirmDelete = async () => {
    if (itemToDelete) {
      try {
        setIsLoading(true);
        await service.delete(itemToDelete);
        await fetchMenus();
      } finally {
        setIsLoading(false);
      }
    }
    closeDeleteDialog();
  };

  const addFilter = (type: FilterType, value: string) => {
    const existingFilterIndex = filters.findIndex((f) => f.type === type);

    if (existingFilterIndex >= 0) {
      const newFilters = [...filters];
      newFilters[existingFilterIndex].value = value;
      setFilters(newFilters);
    } else {
      setFilters((prev) => [...prev, { type, value }]);
    }

    setPagination((prev) => ({
      ...prev,
      page: 1,
    }));
  };

  const removeFilter = (type: FilterType) => {
    setFilters((prev) => prev?.filter((f) => f.type !== type));

    setPagination((prev) => ({
      ...prev,
      page: 1,
    }));
  };

  const handlePageChange = (newPage: number) => {
    setPagination((prev) => ({ ...prev, page: newPage }));
  };

  const handlePageSizeChange = (newPageSize: number) => {
    setPagination((prev) => ({
      ...prev,
      pageSize: newPageSize,
      page: 1,
    }));
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <LoadingSpinner />
      </div>
    );
  }

  const FilterButton = () => (
    <>
      {/* Desktop: Popover */}
      <div className="hidden md:block">
        <Popover>
          <PopoverTrigger asChild>
            <Button
              variant="outline"
              className="h-8 border-[#EE7B11] text-[#EE7B11] hover:bg-[#EE7B11]/10"
            >
              <Filter className="mr-2 h-4 w-4" />
              Filtrar
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-80">
            <FilterContent addFilter={addFilter} />
          </PopoverContent>
        </Popover>
      </div>

      {/* Mobile: Sheet */}
      <div className="md:hidden">
        <Sheet open={isFilterSheetOpen} onOpenChange={setIsFilterSheetOpen}>
          <SheetTrigger asChild>
            <Button
              variant="outline"
              className="h-8 border-[#EE7B11] text-[#EE7B11] hover:bg-[#EE7B11]/10"
            >
              <Filter className="mr-2 h-4 w-4" />
              Filtrar
            </Button>
          </SheetTrigger>
          <SheetContent side="bottom" className="h-[80vh]">
            <SheetHeader>
              <SheetTitle>Filtros</SheetTitle>
            </SheetHeader>
            <div className="mt-4">
              <FilterContent
                addFilter={addFilter}
                onClose={() => setIsFilterSheetOpen(false)}
              />
            </div>
          </SheetContent>
        </Sheet>
      </div>
    </>
  );

  return (
    <div className="p-4 sm:p-6 w-full">
      <Card className="overflow-hidden border-none shadow-md w-full">
        <CardHeader className="bg-gradient-to-r from-[#EE7B11] to-[#f59642] text-white">
          <div className="flex items-center gap-3">
            <Book className="h-6 w-6" />
            <CardTitle className="text-xl">Catálogos</CardTitle>
          </div>
        </CardHeader>
        <CardContent className="p-4 sm:p-6">
          <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4 mb-6">
            <div className="flex flex-wrap gap-2 w-full md:w-auto">
              <FilterButton />
              <div className="flex flex-wrap gap-2">
                {filters?.map((filter) => (
                  <Badge key={filter.type} variant="secondary" className="h-8">
                    Nome: {filter.value}
                    <Button
                      variant="ghost"
                      size="sm"
                      className="ml-2 h-4 w-4 p-0"
                      onClick={() => removeFilter(filter.type)}
                    >
                      <X className="h-3 w-3" />
                    </Button>
                  </Badge>
                ))}
              </div>
            </div>

            <Button
              onClick={() => handleNavigate()}
              size="sm"
              className="bg-[#EE7B11] hover:bg-[#d06a0a] text-white w-full md:w-auto"
            >
              <Plus className="h-4 w-4 mr-2" /> Cadastrar Catálogo
            </Button>
          </div>

          {menus.length > 0 ? (
            <div className="space-y-6">
              <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3">
                {menus.map((menu) => {
                  return (
                    <MenuCard
                      key={menu.id}
                      menu={menu}
                      onEdit={() => handleNavigate(menu.id)}
                      onDelete={() => handleDelete(menu.id)}
                    />
                  );
                })}
              </div>
            </div>
          ) : (
            <div className="text-center py-12 border rounded-lg bg-gray-50 w-full">
              <Book className="h-12 w-12 mx-auto mb-3 text-gray-300" />
              <h3 className="text-lg font-medium text-gray-700 mb-2">
                Nenhum catálogo encontrado
              </h3>
              <p className="text-gray-500 mb-6">
                {filters.length > 0
                  ? "Não encontramos catálogos com os filtros aplicados"
                  : "Você ainda não possui catálogos cadastrados"}
              </p>
              <Button
                onClick={() => handleNavigate()}
                className="bg-[#EE7B11] hover:bg-[#d06a0a] text-white"
              >
                <Plus className="h-4 w-4 mr-2" />
                Cadastrar Catálogo
              </Button>
            </div>
          )}

          {menus.length > 0 && (
            <PaginationControls
              pagination={pagination}
              onPageChange={handlePageChange}
              onPageSizeChange={handlePageSizeChange}
            />
          )}
        </CardContent>
      </Card>

      <DeleteConfirmationAlert
        isOpen={isOpen}
        onClose={closeDeleteDialog}
        onConfirm={handleConfirmDelete}
        title="Confirmar Exclusão de Catálogo"
        message="Tem certeza que deseja excluir este catálogo? Esta ação não pode ser desfeita."
        itemName={
          menus.find((m) => m.id === itemToDelete)?.name || "este catálogo"
        }
      />
    </div>
  );
};

export default Menus;
